import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, money, statusBadge, fileSrc, CATEGORIES } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  AreaChart, Area, PieChart, Pie, Cell, ResponsiveContainer, XAxis, Tooltip,
} from "recharts";
import {
  Plus, X, Building2, DoorOpen, ChevronRight, ChevronLeft, Loader2, Upload,
  Clock, CalendarClock, Wrench, CheckCircle2, FileText, Activity, TrendingUp, User, Inbox, Download, Receipt,
} from "lucide-react";

const inputCls = "w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition";

// Internal funnel status -> friendly client status
const friendly = (tk) => {
  const s = tk.status;
  if (["completed", "completed_no_charge"].includes(s)) return "fCompleted";
  if (s === "cancelled") return "fCancelled";
  if (s === "estimate_sent") return "fAwaiting";
  if (["in_progress", "job_done", "estimate_approved"].includes(s)) return "fInProgress";
  if (tk.scheduled_date) return "fScheduled";
  return "fOpen";
};
const friendlyColor = {
  fOpen: "bg-slate-100 text-slate-700 border-slate-200",
  fScheduled: "bg-indigo-100 text-indigo-800 border-indigo-200",
  fInProgress: "bg-cyan-100 text-cyan-800 border-cyan-200",
  fCompleted: "bg-green-100 text-green-800 border-green-200",
  fAwaiting: "bg-amber-100 text-amber-800 border-amber-200",
  fCancelled: "bg-slate-100 text-slate-400 border-slate-200",
};

const CATS = CATEGORIES;
const PRIOS = ["low", "medium", "high", "urgent"];

const RequestWizard = ({ properties, units, onClose, onDone }) => {
  const { t } = useLang();
  const [step, setStep] = useState(1);
  const [propId, setPropId] = useState("");
  const [propSearch, setPropSearch] = useState("");
  const [unitId, setUnitId] = useState("");
  const [form, setForm] = useState({ title: "", category: "general", priority: "medium", description: "", contact_phone: "", preferred_time: "" });
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const propUnits = units.filter((u) => u.property_id === propId);

  const onPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const ups = await Promise.all(files.map((f) => api.upload(f)));
      setPhotos([...photos, ...ups.map((u) => u.url)]);
    } catch { toast.error(t("error")); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const submit = async () => {
    const unit = units.find((u) => u.id === unitId);
    setBusy(true);
    try {
      await api.post("/tickets", {
        ...form,
        title: form.title || `${t(form.category)} — ${unit?.label || ""}`,
        unit_id: unitId, unit_name: unit?.label || "", client_id: unit?.client_id || null,
        photos,
      });
      toast.success(t("requestCreated"));
      onDone();
    } catch { toast.error(t("error")); setBusy(false); }
  };

  const canNext = step === 1 ? !!propId : step === 2 ? !!unitId : true;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={() => !busy && onClose()}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">{t("requestWizardTitle")}</h2>
            <p className="text-xs text-slate-400">{t("stepLabel")} {step} / 3</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
        </div>

        {/* progress */}
        <div className="flex gap-1.5 px-6 pt-4">
          {[1, 2, 3].map((s) => <div key={s} className={`h-1.5 flex-1 rounded-full ${s <= step ? "bg-orange-500" : "bg-slate-100"}`} />)}
        </div>

        <div className="p-6 space-y-4">
          {step === 1 && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700 flex items-center gap-2"><Building2 className="w-4 h-4 text-orange-500" />{t("selectBuilding")}</label>
              {/* Search bar (helpful when the client has many properties) */}
              <input
                data-testid="wizard-prop-search"
                value={propSearch || ""}
                onChange={(e) => setPropSearch(e.target.value)}
                placeholder={t("search")}
                className={inputCls + " pl-3"}
              />
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {properties
                  .filter((p) => {
                    const q = (propSearch || "").trim().toLowerCase();
                    if (!q) return true;
                    return (p.name || "").toLowerCase().includes(q) || (p.address || "").toLowerCase().includes(q);
                  })
                  .map((p) => (
                  <button key={p.id} data-testid={`wizard-prop-${p.id}`} onClick={() => { setPropId(p.id); setUnitId(""); }}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-colors ${propId === p.id ? "border-orange-500 bg-orange-50" : "border-slate-200 hover:border-slate-300"}`}>
                    <span className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center"><Building2 className="w-4 h-4 text-slate-500" /></span>
                    <div className="flex-1 min-w-0"><div className="font-medium text-slate-900 truncate">{p.name}</div><div className="text-xs text-slate-400 truncate">{p.address}</div></div>
                    {propId === p.id && <CheckCircle2 className="w-5 h-5 text-orange-500" />}
                  </button>
                ))}
                {properties.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">{t("noData")}</p>}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700 flex items-center gap-2"><DoorOpen className="w-4 h-4 text-orange-500" />{t("selectUnitLabel")}</label>
              <div className="grid grid-cols-2 gap-2 max-h-72 overflow-y-auto">
                {propUnits.map((u) => (
                  <button key={u.id} data-testid={`wizard-unit-${u.id}`} onClick={() => setUnitId(u.id)}
                    className={`flex items-center gap-2 p-3 rounded-xl border text-left transition-colors ${unitId === u.id ? "border-orange-500 bg-orange-50" : "border-slate-200 hover:border-slate-300"}`}>
                    <DoorOpen className="w-4 h-4 text-slate-400 shrink-0" />
                    <span className="text-sm font-medium text-slate-800 truncate">{u.label}</span>
                  </button>
                ))}
                {propUnits.length === 0 && <p className="col-span-2 text-sm text-slate-400 py-6 text-center">{t("noData")}</p>}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-slate-500">{t("title")}</label>
                <input data-testid="wizard-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} placeholder={t("title")} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wide text-slate-500">{t("category")}</label>
                  <select data-testid="wizard-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls}>
                    {CATS.map((c) => <option key={c} value={c}>{t(c)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wide text-slate-500">{t("priority")}</label>
                  <select data-testid="wizard-priority" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className={inputCls}>
                    {PRIOS.map((p) => <option key={p} value={p}>{t(p)}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-slate-500">{t("description")}</label>
                <textarea data-testid="wizard-description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wide text-slate-500">{t("contactPhone")}</label>
                  <input data-testid="wizard-phone" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className={inputCls} placeholder="(000) 000-0000" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wide text-slate-500">{t("preferredTime")}</label>
                  <input data-testid="wizard-time" value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} className={inputCls} placeholder="Mon-Fri AM" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1 block">{t("photos") || "Photos"}</label>
                <label className="flex items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-xl py-4 cursor-pointer hover:border-orange-300 text-slate-500 text-sm">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  <input data-testid="wizard-photos" type="file" accept="image/*" multiple className="hidden" onChange={onPhotos} />
                  {uploading ? t("submitting") : t("upload") || "Upload"}
                </label>
                {photos.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {photos.map((p, i) => (
                      <div key={i} className="relative group">
                        <img src={fileSrc(p)} alt="" className="w-14 h-14 rounded-lg object-cover border border-slate-200" />
                        <button type="button" onClick={() => setPhotos(photos.filter((_, j) => j !== i))} className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-slate-900 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-between gap-3 px-6 py-4 border-t border-slate-100 sticky bottom-0 bg-white">
          <button onClick={() => step === 1 ? onClose() : setStep(step - 1)} className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium border border-slate-300 hover:bg-slate-50">
            <ChevronLeft className="w-4 h-4" />{step === 1 ? t("cancel") : t("back")}
          </button>
          {step < 3 ? (
            <button data-testid="wizard-next" disabled={!canNext} onClick={() => setStep(step + 1)} className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50">
              {t("next")}<ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button data-testid="wizard-submit" disabled={busy} onClick={submit} className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}{busy ? t("submitting") : t("submitRequest")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const CAT_HEX = {
  mechanical: "#6366f1", electric: "#f59e0b", plumbing: "#06b6d4", drywall: "#a16207",
  window: "#3b82f6", landscape: "#16a34a", snow: "#0ea5e9", general: "#64748b",
};
const card = "bg-white rounded-2xl shadow-[0_4px_20px_rgb(0,0,0,0.03)] border border-slate-100";
const STEPS = ["received", "fScheduled", "fInProgress", "fCompleted"];
const stageIndex = (f) => f === "fCompleted" ? 3 : f === "fInProgress" ? 2 : f === "fScheduled" ? 1 : 0;

const Stepper = ({ stage }) => {
  const { t } = useLang();
  return (
    <div className="flex items-center">
      {STEPS.map((s, i) => {
        const done = i <= stage;
        return (
          <React.Fragment key={s}>
            <div className="flex flex-col items-center gap-1.5 shrink-0">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${done ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-400"}`}>
                {i < stage ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
              </div>
              <span className={`text-[11px] font-medium ${done ? "text-slate-700" : "text-slate-400"}`}>{t(s === "received" ? "received" : s)}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-1 mb-5 rounded-full ${i < stage ? "bg-orange-500" : "bg-slate-100"}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const RevTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return <div className="bg-slate-900 text-white text-xs rounded-lg px-2.5 py-1.5 shadow-lg">{label}: {payload[0].value}</div>;
};

/* ================= Project request wizard (client-initiated) ================= */
const ProjectRequestWizard = ({ properties, onClose, onDone }) => {
  const [propId, setPropId] = useState("");
  const [form, setForm] = useState({ name: "", description: "" });
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);

  const onPhotos = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6);
    const b64s = await Promise.all(files.map((f) => new Promise((r) => {
      const rd = new FileReader();
      rd.onload = () => r(rd.result);
      rd.readAsDataURL(f);
    })));
    setPhotos((p) => [...p, ...b64s].slice(0, 6));
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (!form.description.trim()) { toast.error("Please describe what you need"); return; }
    setBusy(true);
    try {
      const prop = properties.find((p) => p.id === propId);
      await api.post("/projects", {
        name: form.name.trim(),
        description: form.description.trim(),
        property_id: propId || null,
        property_name: prop?.name || "",
        attachments: photos.map((b64, i) => ({ id: String(i), name: `photo-${i + 1}.jpg`, content_b64: b64 })),
        project_type: "b2b",
      });
      toast.success("Project request submitted — our team will review shortly");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="portal-project-wizard">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Request a new project</h2>
            <div className="text-[11px] text-slate-500">For larger works — remodels, new installs, renovations. For a simple repair use &quot;New request&quot; instead.</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Project name</label>
            <input
              data-testid="portal-project-name"
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Kitchen remodel — Main office"
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Property</label>
            <select value={propId} onChange={(e) => setPropId(e.target.value)} className={inputCls}>
              <option value="">— select property —</option>
              {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Description of the work</label>
            <textarea
              data-testid="portal-project-description"
              value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows="5"
              placeholder="Describe the scope, expected outcome, timing constraints, budget hints..."
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Photos or plans (up to 6)</label>
            <input type="file" accept="image/*" multiple onChange={onPhotos} className="text-xs" data-testid="portal-project-photos" />
            {photos.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
                {photos.map((p, i) => (
                  <div key={i} className="relative">
                    <img src={p} alt="" className="w-full h-20 object-cover rounded border" />
                    <button onClick={() => setPhotos(photos.filter((_, idx) => idx !== i))}
                      className="absolute top-1 right-1 bg-rose-600 text-white rounded-full p-0.5"><X className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
          <button onClick={onClose} disabled={busy} className="text-xs px-4 py-2 rounded-lg border border-slate-300 hover:bg-white">Cancel</button>
          <button
            data-testid="portal-project-submit"
            onClick={submit} disabled={busy}
            className="text-xs px-5 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-bold disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Submit for review
          </button>
        </div>
      </div>
    </div>
  );
};

/* ================= Client project approve/reject modal ================= */
const ProjectDecideModal = ({ data, setData, onClose, onDone }) => {
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!data.name.trim()) { toast.error("Please enter your name"); return; }
    if (data.decision === "reject" && !data.reason.trim()) { toast.error("Please explain what needs to change"); return; }
    setBusy(true);
    try {
      await api.post(`/projects/${data.project.id}/transitions/client-decide`, {
        decision: data.decision,
        signature_name: data.name.trim(),
        reason: data.reason.trim(),
      });
      toast.success(data.decision === "approve" ? "Approved — thank you!" : "Feedback sent to our team");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const approve = data.decision === "approve";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="portal-project-decide">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">{approve ? "Approve project" : "Request changes"}</h2>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="text-sm font-semibold">{data.project.name}</div>
            <div className="text-xs text-slate-500">{data.project.number} · {money(data.project.contract_value)}</div>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Your name (for the record)</label>
            <input
              data-testid="portal-decide-name"
              value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })}
              className={inputCls}
            />
          </div>
          {!approve && (
            <div>
              <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">What needs to change?</label>
              <textarea
                data-testid="portal-decide-reason"
                value={data.reason} onChange={(e) => setData({ ...data, reason: e.target.value })}
                rows="4" className={inputCls}
              />
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200">
          <button onClick={onClose} disabled={busy} className="text-xs px-4 py-2 rounded-lg border border-slate-300">Cancel</button>
          <button
            data-testid="portal-decide-submit"
            onClick={submit} disabled={busy}
            className={`text-xs px-5 py-2 rounded-lg font-bold text-white disabled:opacity-50 inline-flex items-center gap-1.5 ${approve ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}`}
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : approve ? <CheckCircle2 className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
            {approve ? "Confirm approval" : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function ClientPortal() {
  const { t } = useLang();
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const navigate = useNavigate();
  const [estimates, setEstimates] = useState([]);
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [wizard, setWizard] = useState(false);
  const [projectWizard, setProjectWizard] = useState(false);
  const [projects, setProjects] = useState([]);
  const [rejectDialog, setRejectDialog] = useState(null); // { est, reason }
  const [projectDecideDialog, setProjectDecideDialog] = useState(null); // { project, decision, reason, name }

  const load = () => {
    api.get("/tickets").then(setTickets).catch(() => {});
    api.get("/estimates").then(setEstimates).catch(() => {});
    api.get("/projects").then(setProjects).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get("/properties").then(setProperties).catch(() => {});
    api.get("/units").then(setUnits).catch(() => {});
  }, []);

  const changeEst = async (est, status, reason) => {
    try { await api.post(`/estimates/${est.id}/status`, { status, reason }); toast.success(t("saved")); load(); }
    catch { toast.error(t("error")); }
  };

  const submitReject = async () => {
    if (!rejectDialog.reason?.trim()) return toast.error(t("rejectReasonRequired"));
    await changeEst(rejectDialog.est, "rejected", rejectDialog.reason.trim());
    setRejectDialog(null);
  };

  const downloadInvoice = async (est) => {
    try {
      const blob = await api.getBlob(`/estimates/${est.id}/pdf`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${est.number || "invoice"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error(t("error")); }
  };

  const invoices = estimates.filter((e) => ["billed", "paid"].includes(e.status))
    .sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));

  const counts = { fOpen: 0, fScheduled: 0, fInProgress: 0, fCompleted: 0 };
  tickets.forEach((tk) => { const f = friendly(tk); if (counts[f] !== undefined) counts[f]++; });
  const pendingEstimates = estimates.filter((e) => e.status === "sent");
  const approvedSpend = estimates.filter((e) => ["approved", "executed", "billed", "paid"].includes(e.status)).reduce((s, e) => s + (e.total || 0), 0);
  const pendingSpend = pendingEstimates.reduce((s, e) => s + (e.total || 0), 0);

  // this week new
  const weekAgo = new Date(Date.now() - 7 * 864e5).toISOString();
  const newThisWeek = tickets.filter((tk) => (tk.created_at || "") >= weekAgo).length;

  // active ticket for stepper
  const active = tickets.find((tk) => !["completed", "completed_no_charge", "cancelled"].includes(tk.status));

  // requests over time (last 6 months)
  const months = [];
  const base = new Date();
  for (let i = 5; i >= 0; i--) {
    const d = new Date(base.getFullYear(), base.getMonth() - i, 1);
    months.push({ key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: d.toLocaleString(undefined, { month: "short" }), value: 0 });
  }
  tickets.forEach((tk) => { const k = (tk.created_at || "").slice(0, 7); const m = months.find((x) => x.key === k); if (m) m.value++; });

  // category donut
  const catMap = {};
  tickets.forEach((tk) => { const c = tk.category || "general"; catMap[c] = (catMap[c] || 0) + 1; });
  const donut = Object.entries(catMap).map(([k, v]) => ({ name: t(k), value: v, color: CAT_HEX[k] || "#94a3b8" })).sort((a, b) => b.value - a.value);

  // upcoming visits
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = tickets.filter((tk) => (tk.scheduled_date || "").slice(0, 10) >= today).sort((a, b) => (a.scheduled_date || "").localeCompare(b.scheduled_date || "")).slice(0, 4);

  // activity timeline (most recently updated)
  const activity = [...tickets]
    .filter((tk) => !statusFilter || friendly(tk) === statusFilter)
    .sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""))
    .slice(0, 15);

  const kpis = [
    { key: "fOpen", label: t("fOpen"), icon: Clock, color: "text-amber-600", bg: "bg-amber-50", val: counts.fOpen },
    { key: "fScheduled", label: t("fScheduled"), icon: CalendarClock, color: "text-blue-600", bg: "bg-blue-50", val: counts.fScheduled },
    { key: "fInProgress", label: t("fInProgress"), icon: Wrench, color: "text-violet-600", bg: "bg-violet-50", val: counts.fInProgress },
    { key: "fCompleted", label: t("fCompleted"), icon: CheckCircle2, color: "text-green-600", bg: "bg-green-50", val: counts.fCompleted },
  ];

  return (
    <div className="space-y-6">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-orange-600 to-orange-500 text-white p-6 sm:p-8 shadow-lg">
        <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-white/10" />
        <div className="absolute right-16 bottom-0 w-32 h-32 rounded-full bg-white/5" />
        <div className="relative flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-2xl md:text-3xl font-extrabold tracking-tight">{t("portalGreeting")}, {user.name}</h1>
            <p className="text-orange-50/90 text-sm mt-1">{t("portalHint")}</p>
            <div className="flex gap-5 mt-4">
              <div><div className="text-2xl font-bold tabular-nums">{tickets.length}</div><div className="text-xs text-orange-50/80">{t("totalRequests")}</div></div>
              <div className="border-l border-white/20 pl-5"><div className="text-2xl font-bold tabular-nums">{counts.fInProgress + counts.fScheduled}</div><div className="text-xs text-orange-50/80">{t("fInProgress")} + {t("fScheduled")}</div></div>
              <div className="border-l border-white/20 pl-5"><div className="text-2xl font-bold tabular-nums">+{newThisWeek}</div><div className="text-xs text-orange-50/80">{t("thisWeekShort")}</div></div>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button data-testid="new-property-btn" onClick={() => navigate("/properties?new=1")}
              className="inline-flex items-center gap-2 rounded-full bg-white/15 text-white text-sm font-bold px-5 py-3 hover:bg-white/25 transition-colors border border-white/30">
              <Building2 className="w-4 h-4" /> {t("addProperty") || "Add property"}
            </button>
            <button data-testid="new-project-btn" onClick={() => setProjectWizard(true)}
              className="inline-flex items-center gap-2 rounded-full bg-white/15 text-white text-sm font-bold px-5 py-3 hover:bg-white/25 transition-colors border border-white/30">
              <Plus className="w-4 h-4" /> Request new project
            </button>
            <button data-testid="new-request-btn" onClick={() => setWizard(true)} className="inline-flex items-center gap-2 rounded-full bg-white text-orange-700 text-sm font-bold px-5 py-3 hover:bg-orange-50 transition-colors shadow-md">
              <Plus className="w-4 h-4" /> {t("newRequest")}
            </button>
          </div>
        </div>
      </div>

      {/* KPI cards — each one filters the ticket list below by its status */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((c) => {
          const active = statusFilter === c.key;
          return (
            <button
              key={c.key}
              type="button"
              data-testid={`portal-card-${c.key}`}
              onClick={() => setStatusFilter(active ? "" : c.key)}
              className={`${card} p-5 text-left transition hover:shadow-md hover:-translate-y-0.5 ${active ? "ring-2 ring-orange-500 border-orange-300" : ""}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wide text-slate-500">{c.label}</span>
                <span className={`w-9 h-9 rounded-full flex items-center justify-center ${c.bg}`}><c.icon className={`w-4 h-4 ${c.color}`} strokeWidth={1.5} /></span>
              </div>
              <div className="text-3xl font-bold text-slate-900 mt-2 tabular-nums">{c.val}</div>
              <div className="text-[10px] mt-1 text-slate-400">{active ? (t("filtering") || "Filtering") : (t("clickToFilter") || "Click to filter")}</div>
            </button>
          );
        })}
      </div>

      {/* Filtered list — appears right below the KPI when the user clicks one */}
      {statusFilter && (() => {
        const filtered = tickets.filter((tk) => friendly(tk) === statusFilter)
          .sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
        const label = kpis.find((k) => k.key === statusFilter)?.label || statusFilter;
        return (
          <div className={`${card} p-4`} data-testid="filtered-ticket-list">
            <div className="flex items-center justify-between mb-3">
              <div className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-50 text-orange-700 border border-orange-200 px-3 py-1 text-xs font-bold uppercase tracking-wide">
                  {label} · {filtered.length}
                </span>
                <span className="text-xs text-slate-500 font-normal">
                  {t("showingFiltered") || "Showing filtered service calls"}
                </span>
              </div>
              <button
                data-testid="portal-clear-filter"
                onClick={() => setStatusFilter("")}
                className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
              >
                <span className="text-base leading-none">×</span>
                {t("clearFilter") || "Clear filter"}
              </button>
            </div>
            {filtered.length === 0 ? (
              <div className="py-8 text-center text-slate-400 text-sm">{t("noData")}</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {filtered.map((tk) => (
                  <div
                    key={tk.id}
                    data-testid={`filtered-ticket-${tk.id}`}
                    className="py-3 flex items-center justify-between gap-3 cursor-pointer hover:bg-slate-50 -mx-4 px-4 transition-colors"
                    onClick={() => navigate(`/tickets?open=${tk.id}`)}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm text-slate-900 truncate">{tk.title || tk.number}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {tk.property_name}{tk.unit_name ? ` · ${tk.unit_name}` : ""}
                        {tk.scheduled_date ? ` · ${new Date(tk.scheduled_date).toLocaleDateString()}` : ""}
                      </div>
                    </div>
                    <span className={`text-[10px] font-bold uppercase tracking-wide rounded-full px-2.5 py-1 border ${friendlyColor[friendly(tk)] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
                      {t(friendly(tk))}
                    </span>
                    <button
                      type="button"
                      data-testid={`filtered-open-${tk.id}`}
                      onClick={(e) => { e.stopPropagation(); navigate(`/tickets?open=${tk.id}`); }}
                      className="inline-flex items-center gap-1 text-xs font-semibold border border-slate-300 rounded-lg px-3 py-1.5 hover:bg-slate-900 hover:text-white hover:border-slate-900 text-slate-700 transition-colors"
                    >
                      {t("open") || "Open"} <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Stepper + Estimates */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className={`${card} p-6 lg:col-span-8`} data-testid="active-request">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-800">{t("statusTracker")}</h2>
            {active && <span className="text-xs text-slate-400 font-mono">{active.number}</span>}
          </div>
          {active ? (
            <>
              <div className="mb-5">
                <div className="font-semibold text-slate-900">{active.title}</div>
                <div className="text-xs text-slate-400">{active.unit_name} · {t(active.category)}{active.assigned_to_name ? ` · ${active.assigned_to_name}` : ""}</div>
              </div>
              <Stepper stage={stageIndex(friendly(active))} />
            </>
          ) : (
            <div className="py-10 text-center text-slate-400"><Inbox className="w-10 h-10 mx-auto mb-2 text-slate-200" />{t("noActiveRequests")}</div>
          )}
        </div>

        <div className={`${card} overflow-hidden lg:col-span-4`} data-testid="portal-estimates">
          <div className="px-5 py-4 border-b border-slate-100">
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2"><FileText className="w-4 h-4 text-amber-500" strokeWidth={1.5} />{t("estimatesToApprove")}</h2>
            <div className="flex gap-4 mt-3">
              <div><div className="text-[10px] uppercase font-bold text-slate-400">{t("approvedSpend")}</div><div className="text-lg font-bold text-green-700 tabular-nums">{money(approvedSpend)}</div></div>
              <div className="border-l border-slate-100 pl-4"><div className="text-[10px] uppercase font-bold text-slate-400">{t("pendingSpend")}</div><div className="text-lg font-bold text-amber-600 tabular-nums">{money(pendingSpend)}</div></div>
            </div>
          </div>
          <div className="divide-y divide-slate-50 max-h-56 overflow-y-auto">
            {pendingEstimates.length === 0 && <div className="px-5 py-8 text-center text-slate-400 text-sm">{t("noEstimatesApprove")}</div>}
            {(() => {
              // Group pending estimates: Service Call first, then Projects. Header row before each group.
              const svc = pendingEstimates.filter((e) => e.source_type !== "project");
              const prj = pendingEstimates.filter((e) => e.source_type === "project");
              const group = (rows, label, color) => rows.length ? (
                <React.Fragment>
                  <div className={`px-5 py-1.5 text-[10px] font-bold uppercase tracking-wider ${color} border-l-2`}>
                    {label} ({rows.length})
                  </div>
                  {rows.map((e) => (
                    <div key={e.id} data-testid={`portal-estimate-${e.id}`} className="px-5 py-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-medium text-slate-900 truncate text-sm">{e.title}</div>
                          <div className="text-xs text-slate-400">
                            {e.number}{e.source_type === "project" && e.project_number && ` · ${e.project_number}`}
                          </div>
                        </div>
                        <span className="text-sm font-bold text-slate-900 tabular-nums shrink-0">{money(e.total)}</span>
                      </div>
                      <div className="flex gap-2 mt-2">
                        <button data-testid={`portal-approve-${e.id}`} onClick={() => changeEst(e, "approved")} className="flex-1 text-xs font-semibold bg-green-600 text-white rounded-lg py-1.5 hover:bg-green-700">{t("approve")}</button>
                        <button data-testid={`portal-reject-${e.id}`} onClick={() => setRejectDialog({ est: e, reason: "" })} className="flex-1 text-xs font-semibold border border-slate-300 text-slate-600 rounded-lg py-1.5 hover:bg-slate-50">{t("reject")}</button>
                      </div>
                    </div>
                  ))}
                </React.Fragment>
              ) : null;
              return <>{group(svc, "Service Calls", "text-blue-700 bg-blue-50/60 border-blue-300")}{group(prj, "Projects", "text-indigo-700 bg-indigo-50/60 border-indigo-300")}</>;
            })()}
          </div>
        </div>
      </div>

      {/* Invoices — split by source: Service Calls vs Projects */}
      {(() => {
        const invServiceCall = invoices.filter((e) => e.source_type !== "project");
        const invProject = invoices.filter((e) => e.source_type === "project");
        const renderRow = (e, testid) => (
          <div key={e.id} data-testid={testid} className="flex flex-wrap items-center gap-3 px-5 py-3">
            <div className="flex-1 min-w-0">
              <div className="font-medium text-slate-900 truncate text-sm">{e.title}</div>
              <div className="text-xs text-slate-400">
                {e.number}
                {e.source_type === "project" && e.project_number && <> · <b>{e.project_number}</b></>}
                {e.status === "paid" && e.paid_date ? ` · ${t("paidOn")} ${e.paid_date.slice(0, 10)}` : ""}
              </div>
            </div>
            <span className="text-sm font-bold text-slate-900 tabular-nums">{money(e.total)}</span>
            <span data-testid={`invoice-status-${e.id}`} className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border ${e.status === "paid" ? "bg-green-100 text-green-800 border-green-200" : "bg-amber-100 text-amber-800 border-amber-200"}`}>
              {e.status === "paid" ? t("paid") : t("toPay")}
            </span>
            <button data-testid={`invoice-pdf-${e.id}`} onClick={() => downloadInvoice(e)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold border border-slate-300 rounded-lg px-3 py-1.5 hover:bg-slate-50 text-slate-700">
              <Download className="w-3.5 h-3.5" />{t("downloadInvoice")}
            </button>
          </div>
        );
        return (
          <div className="space-y-4">
            {/* Service Call invoices */}
            <div className={`${card} overflow-hidden`} data-testid="portal-invoices-service">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <Receipt className="w-4 h-4 text-green-600" strokeWidth={1.5} />
                  {t("invoices")} <span className="text-xs font-medium text-slate-500">— Service Calls ({invServiceCall.length})</span>
                </h2>
              </div>
              <div className="divide-y divide-slate-50">
                {invServiceCall.length === 0
                  ? <div className="px-5 py-8 text-center text-slate-400 text-sm">{t("noInvoices")}</div>
                  : invServiceCall.map((e) => renderRow(e, `portal-invoice-${e.id}`))}
              </div>
            </div>

            {/* Project invoices — only rendered if any exist */}
            {invProject.length > 0 && (
              <div className={`${card} overflow-hidden`} data-testid="portal-invoices-project">
                <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                  <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                    <Receipt className="w-4 h-4 text-indigo-600" strokeWidth={1.5} />
                    {t("invoices")} <span className="text-xs font-medium text-slate-500">— Projects ({invProject.length})</span>
                  </h2>
                </div>
                <div className="divide-y divide-slate-50">
                  {invProject.map((e) => renderRow(e, `portal-invoice-project-${e.id}`))}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Projects awaiting your approval (approve/reject workflow) */}
      {(() => {
        const awaiting = projects.filter((p) => p.status === "pending_client_approval");
        if (awaiting.length === 0) return null;
        return (
          <div className={`${card} overflow-hidden border-2 border-indigo-200`} data-testid="portal-awaiting-approval">
            <div className="px-5 py-4 border-b border-slate-100 bg-indigo-50/50">
              <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-indigo-600" strokeWidth={1.5} />
                Projects awaiting your approval
                <span className="text-xs font-medium text-indigo-700">({awaiting.length})</span>
              </h2>
            </div>
            <div className="divide-y divide-slate-50">
              {awaiting.map((p) => (
                <div key={p.id} className="px-5 py-4 flex flex-wrap items-center gap-3" data-testid={`portal-approval-project-${p.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{p.name}</div>
                    <div className="text-xs text-slate-500">{p.number} · {p.property_name}</div>
                    {p.description && <div className="text-xs text-slate-600 mt-1 line-clamp-2">{p.description}</div>}
                  </div>
                  <span className="text-lg font-bold text-slate-900 tabular-nums">{money(p.contract_value)}</span>
                  <button
                    data-testid={`portal-approve-project-${p.id}`}
                    onClick={() => setProjectDecideDialog({ project: p, decision: "approve", reason: "", name: user.name || "" })}
                    className="text-xs font-bold px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1.5"
                  ><CheckCircle2 className="w-3.5 h-3.5" /> Approve</button>
                  <button
                    data-testid={`portal-reject-project-${p.id}`}
                    onClick={() => setProjectDecideDialog({ project: p, decision: "reject", reason: "", name: user.name || "" })}
                    className="text-xs font-bold px-4 py-2 rounded-lg border-2 border-rose-300 text-rose-700 hover:bg-rose-50 inline-flex items-center gap-1.5"
                  ><X className="w-3.5 h-3.5" /> Reject</button>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Project Proposals — accept/reject same pattern as service call estimates */}
      {(() => {
        const backend = process.env.REACT_APP_BACKEND_URL || "";
        const withProp = projects.filter((p) => p.proposal_public_url);
        if (!withProp.length) return null;
        const pending = withProp.filter((p) => (p.proposal || {}).status === "sent");
        const decided = withProp.filter((p) => ["accepted", "rejected"].includes((p.proposal || {}).status));
        return (
          <div className={`${card} overflow-hidden`} data-testid="portal-project-proposals">
            <div className="px-5 py-4 border-b border-slate-100">
              <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <FileText className="w-4 h-4 text-indigo-600" strokeWidth={1.5} />
                Project Proposals
                <span className="text-xs font-medium text-slate-500">({withProp.length})</span>
              </h2>
            </div>
            <div className="divide-y divide-slate-50">
              {pending.length === 0 && decided.length === 0 && (
                <div className="px-5 py-8 text-center text-slate-400 text-sm">No project proposals yet.</div>
              )}
              {pending.map((p) => (
                <div key={p.id} data-testid={`portal-proposal-${p.id}`} className="px-5 py-3 flex flex-wrap items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 truncate text-sm">{p.name}</div>
                    <div className="text-xs text-slate-400">
                      {p.number} · sent {(p.proposal?.sent_at || "").slice(0, 10)}
                    </div>
                  </div>
                  <span className="text-sm font-bold text-slate-900 tabular-nums">{money(p.contract_value)}</span>
                  <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border bg-amber-100 text-amber-800 border-amber-200">
                    Awaiting your review
                  </span>
                  <a
                    data-testid={`portal-proposal-open-${p.id}`}
                    href={`${backend}${p.proposal_public_url}`}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-bold bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-3 py-1.5"
                  >
                    Review &amp; sign <Download className="w-3.5 h-3.5" />
                  </a>
                </div>
              ))}
              {decided.map((p) => (
                <div key={p.id} data-testid={`portal-proposal-${p.id}`} className="px-5 py-3 flex flex-wrap items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 truncate text-sm">{p.name}</div>
                    <div className="text-xs text-slate-400">{p.number}</div>
                  </div>
                  <span className="text-sm font-bold text-slate-900 tabular-nums">{money(p.contract_value)}</span>
                  <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border ${p.proposal.status === "accepted"
                    ? "bg-green-100 text-green-800 border-green-200"
                    : "bg-red-50 text-red-700 border-red-200"}`}>
                    {p.proposal.status === "accepted" ? "Accepted" : "Rejected"}
                  </span>
                  <a
                    href={`${backend}${p.proposal_public_url}`}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold border border-slate-300 rounded-lg px-3 py-1.5 hover:bg-slate-50 text-slate-700"
                  >View</a>
                </div>
              ))}
            </div>
          </div>
        );
      })()}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className={`${card} p-6`} data-testid="chart-overtime">
          <h2 className="text-lg font-bold text-slate-800 mb-1 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-orange-500" strokeWidth={1.5} />{t("requestsOverTime")}</h2>
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%" minHeight={140}>
              <AreaChart data={months} margin={{ top: 8, right: 5, left: -20, bottom: 0 }}>
                <defs><linearGradient id="catGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#ea580c" stopOpacity={0.3} /><stop offset="100%" stopColor="#ea580c" stopOpacity={0} /></linearGradient></defs>
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <Tooltip content={<RevTooltip />} />
                <Area type="monotone" dataKey="value" stroke="#ea580c" strokeWidth={2.5} fill="url(#catGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className={`${card} p-6`} data-testid="chart-category">
          <h2 className="text-lg font-bold text-slate-800 mb-2">{t("categoriesTitle")}</h2>
          <div className="flex items-center gap-4">
            <div className="w-36 h-36 shrink-0">
              <ResponsiveContainer width="100%" height="100%" minHeight={100}>
                <PieChart>
                  <Pie data={donut} dataKey="value" nameKey="name" innerRadius={42} outerRadius={62} paddingAngle={2} stroke="none">
                    {donut.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 space-y-1.5">
              {donut.slice(0, 6).map((d, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span className="flex items-center gap-1.5 text-slate-600"><span className="w-2.5 h-2.5 rounded-full" style={{ background: d.color }} />{d.name}</span>
                  <span className="text-slate-400 tabular-nums">{d.value}</span>
                </div>
              ))}
              {donut.length === 0 && <p className="text-sm text-slate-400">{t("noData")}</p>}
            </div>
          </div>
        </div>
      </div>

      {/* Upcoming visits + Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className={`${card} overflow-hidden`} data-testid="upcoming-visits">
          <div className="px-5 py-4 border-b border-slate-100"><h2 className="text-lg font-bold text-slate-800 flex items-center gap-2"><CalendarClock className="w-4 h-4 text-blue-500" strokeWidth={1.5} />{t("upcomingSchedule")}</h2></div>
          <div className="divide-y divide-slate-50">
            {upcoming.length === 0 && <div className="px-5 py-8 text-center text-slate-400 text-sm">{t("nothingScheduled")}</div>}
            {upcoming.map((u) => (
              <div key={u.id} data-testid={`visit-${u.id}`} className="flex items-center gap-3 px-5 py-3">
                <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex flex-col items-center justify-center shrink-0">
                  <span className="text-[10px] uppercase font-bold leading-none">{new Date(u.scheduled_date).toLocaleString(undefined, { month: "short" })}</span>
                  <span className="text-base font-bold leading-none">{new Date(u.scheduled_date).getDate()}</span>
                </div>
                <div className="flex-1 min-w-0"><div className="font-medium text-slate-900 truncate text-sm">{u.title}</div><div className="text-xs text-slate-400 truncate flex items-center gap-1">{u.assigned_to_name && <><User className="w-3 h-3" />{u.assigned_to_name} · </>}{u.unit_name}</div></div>
              </div>
            ))}
          </div>
        </div>

        <div className={`${card} overflow-hidden`} data-testid="activity-timeline">
          <div className="px-5 py-4 border-b border-slate-100"><h2 className="text-lg font-bold text-slate-800 flex items-center gap-2"><Activity className="w-4 h-4 text-violet-500" strokeWidth={1.5} />{t("recentActivity")}</h2></div>
          <div className="px-5 py-3">
            {activity.length === 0 && <div className="py-6 text-center text-slate-400 text-sm">{t("noData")}</div>}
            <div className="space-y-0">
              {activity.map((a, i) => {
                const f = friendly(a);
                return (
                  <div key={a.id} data-testid={`activity-${a.id}`} className="flex gap-3 pb-4 last:pb-0 relative">
                    {i < activity.length - 1 && <div className="absolute left-[5px] top-4 bottom-0 w-px bg-slate-100" />}
                    <span className={`w-2.5 h-2.5 rounded-full mt-1.5 shrink-0 ${f === "fCompleted" ? "bg-green-500" : f === "fInProgress" ? "bg-violet-500" : f === "fScheduled" ? "bg-blue-500" : "bg-amber-500"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-800 truncate"><span className="font-medium">{a.title}</span></div>
                      <div className="text-xs text-slate-400">{t(f)} · {(a.updated_at || "").slice(0, 10)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {wizard && <RequestWizard properties={properties} units={units} onClose={() => setWizard(false)} onDone={() => { setWizard(false); load(); }} />}

      {projectWizard && (
        <ProjectRequestWizard
          properties={properties}
          onClose={() => setProjectWizard(false)}
          onDone={() => { setProjectWizard(false); load(); }}
        />
      )}

      {projectDecideDialog && (
        <ProjectDecideModal
          data={projectDecideDialog}
          setData={setProjectDecideDialog}
          onClose={() => setProjectDecideDialog(null)}
          onDone={() => { setProjectDecideDialog(null); load(); }}
        />
      )}

      {rejectDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={() => setRejectDialog(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">{t("rejectEstimate")}</h2>
              <button onClick={() => setRejectDialog(null)} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="text-sm font-medium text-slate-900">{rejectDialog.est.title}</div>
                <div className="text-xs text-slate-400">{rejectDialog.est.number} · {money(rejectDialog.est.total)}</div>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("rejectReason")}</label>
                <textarea data-testid="portal-reject-reason" rows={4} value={rejectDialog.reason} onChange={(e) => setRejectDialog({ ...rejectDialog, reason: e.target.value })} placeholder={t("rejectReasonHint")} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 outline-none" autoFocus />
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200">
              <button onClick={() => setRejectDialog(null)} className="rounded-lg px-4 py-2 text-sm font-medium border border-slate-300 hover:bg-slate-50">{t("cancel")}</button>
              <button data-testid="portal-reject-confirm" onClick={submitReject} className="rounded-lg px-4 py-2 text-sm font-medium bg-red-600 text-white hover:bg-red-700">{t("confirmReject")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
